"use client";

import type { ReactNode } from "react";
import Heading from "@/component/ui/Heading";
import {
    CollapsibleContent,
    type CollapsibleContentProps,
} from "@farm-junction/design-library/components/CollapsibleContent/CollapsibleContent";

export type BodyContentLibProps = {
    heading?: string;
    headingTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
    headingClassName?: string;
    fullText?: string;
    children?: ReactNode;
    containerClassName?: string;
    className?: string;
    collapsedHeight?: number;
    moreLabel?: string;
    lessLabel?: string;
    mode?: CollapsibleContentProps["mode"];
    iconForClosed?: CollapsibleContentProps["iconForClosed"];
    iconForOpen?: CollapsibleContentProps["iconForOpen"];
    slotProps?: CollapsibleContentProps["slotProps"];
};

const mergeClass = (...classes: Array<string | undefined>) => classes.filter(Boolean).join(" ");

const DEFAULT_CONTENT_CLASS =
    "font-sans [&_h2]:text-[#373737] [&_h2]:text-[22px] [&_h2]:leading-[28px] [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-[#373737] [&_h3]:text-[18px] [&_h3]:leading-[24px] [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_:is(h1,h2,h3,h4,h5,h6)]:bg-[#f5f5f5] [&_:is(h1,h2,h3,h4,h5,h6)]:border-l-[2px] [&_:is(h1,h2,h3,h4,h5,h6)]:border-l-[#006db7] [&_:is(h1,h2,h3,h4,h5,h6)]:p-2";

const DEFAULT_SLOT_PROPS: NonNullable<CollapsibleContentProps["slotProps"]> = {
    buttonProps: {
        className: "text-[#006db7] gap-1 px-0 cursor-pointer",
    },
    iconProps: {
        className: "h-[10px] w-3 relative top-[2px]",
        fill: "#006db7",
    },
    textProps: {
        className: "text-[#373737] text-[16px] leading-[24px]",
    },
};

export default function BodyContentLib({
    heading,
    headingTag = "h2",
    headingClassName = "mb-4",
    fullText,
    children,
    containerClassName,
    className,
    collapsedHeight = 120,
    moreLabel = "Read More",
    lessLabel = "Read Less",
    mode = "height",
    iconForClosed = "rightCaret",
    iconForOpen = "rightCaret",
    slotProps,
}: BodyContentLibProps) {
    const content = children ?? (fullText ? <div dangerouslySetInnerHTML={{ __html: fullText }} /> : null);

    if (!heading && !content) return null;

    return (
        <div className={containerClassName}>
            {heading && (
                <Heading tag={headingTag} className={`text-[#101828] ${headingClassName}`}>
                    {heading}
                </Heading>
            )}
            {content && (
                <CollapsibleContent
                    className={mergeClass(DEFAULT_CONTENT_CLASS, className)}
                    collapsedHeight={collapsedHeight}
                    iconForClosed={iconForClosed}
                    iconForOpen={iconForOpen}
                    lessLabel={lessLabel}
                    mode={mode}
                    moreLabel={moreLabel}
                    slotProps={{
                        ...DEFAULT_SLOT_PROPS,
                        ...slotProps,
                        buttonProps: {
                            ...DEFAULT_SLOT_PROPS.buttonProps,
                            ...slotProps?.buttonProps,
                            className: mergeClass(DEFAULT_SLOT_PROPS.buttonProps?.className, slotProps?.buttonProps?.className),
                        },
                        iconProps: {
                            ...DEFAULT_SLOT_PROPS.iconProps,
                            ...slotProps?.iconProps,
                            className: mergeClass(DEFAULT_SLOT_PROPS.iconProps?.className, slotProps?.iconProps?.className),
                        },
                        textProps: {
                            ...DEFAULT_SLOT_PROPS.textProps,
                            ...slotProps?.textProps,
                            className: mergeClass(DEFAULT_SLOT_PROPS.textProps?.className, slotProps?.textProps?.className),
                        },
                    }}
                >
                    {content}
                </CollapsibleContent>
            )}
        </div>
    );
}
